import React, { useState, useEffect } from 'react';
import { Eye, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const IndividualMessagesTable = ({ onPreview }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(''); // '' = all, 'PENDING_APPROVAL', 'APPROVED', etc.

  useEffect(() => {
    fetchMessages();
  }, [filter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = filter 
        ? `${API_URL}/api/email-campaigns/messages/review?status=${filter}`
        : `${API_URL}/api/email-campaigns/messages/review`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Error al cargar mensajes');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/email-campaigns/messages/${messageId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Mensaje aprobado exitosamente');
      fetchMessages();
    } catch (error) {
      console.error('Error approving message:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar mensaje');
    }
  };

  const handleSendNow = async (messageId) => {
    if (!window.confirm('¿Estás seguro de enviar este mensaje ahora?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/email-campaigns/messages/${messageId}/send_now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Mensaje encolado para envío');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar mensaje');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING_APPROVAL: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' },
      APPROVED: { label: 'Aprobado', className: 'bg-green-100 text-green-800' },
      SENT: { label: 'Enviado', className: 'bg-blue-100 text-blue-800' },
      FAILED: { label: 'Fallido', className: 'bg-red-100 text-red-800' },
      CANCELLED: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800' }
    };

    const config = statusConfig[status] || statusConfig.PENDING_APPROVAL;
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getRecipientTypeBadge = (type) => {
    const typeConfig = {
      CLIENTE: { label: 'Cliente', className: 'bg-blue-50 text-blue-700' },
      POLIZA: { label: 'Póliza', className: 'bg-purple-50 text-purple-700' },
      LEAD: { label: 'Lead', className: 'bg-green-50 text-green-700' }
    };

    const config = typeConfig[type] || typeConfig.CLIENTE;
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const canApprove = (message) => {
    return message.status === 'PENDING_APPROVAL';
  };

  const canSend = (message) => {
    return message.status === 'PENDING_APPROVAL' || message.status === 'APPROVED';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-slate-500">Cargando mensajes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => setFilter('')}
          variant={filter === '' ? 'default' : 'outline'}
          size="sm"
        >
          Todos
        </Button>
        <Button
          onClick={() => setFilter('PENDING_APPROVAL')}
          variant={filter === 'PENDING_APPROVAL' ? 'default' : 'outline'}
          size="sm"
        >
          Pendientes
        </Button>
        <Button
          onClick={() => setFilter('APPROVED')}
          variant={filter === 'APPROVED' ? 'default' : 'outline'}
          size="sm"
        >
          Aprobados
        </Button>
        <Button
          onClick={() => setFilter('SENT')}
          variant={filter === 'SENT' ? 'default' : 'outline'}
          size="sm"
        >
          Enviados
        </Button>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-slate-200 rounded-lg">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Asunto
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Destinatario
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Fecha Programada
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {messages.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                  No hay mensajes programados
                </td>
              </tr>
            ) : (
              messages.map((message) => (
                <tr key={message.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-900">
                      {message.campaign_name || 'Sin nombre'}
                    </div>
                    <div className="text-sm text-slate-500">
                      {message.campaign_subject || 'Sin asunto'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-900">
                      {message.recipient_name || 'Sin nombre'}
                    </div>
                    <div className="text-sm text-slate-500">
                      {message.recipient_email}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getRecipientTypeBadge(message.recipient_type)}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {message.scheduled_at 
                      ? new Date(message.scheduled_at).toLocaleString('es-ES')
                      : 'No programado'
                    }
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(message.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {/* Botón Preview - siempre disponible */}
                      <Button
                        onClick={() => onPreview(message)}
                        variant="outline"
                        size="sm"
                        title="Previsualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>

                      {/* Botón Aprobar - solo si está PENDING_APPROVAL */}
                      {canApprove(message) && (
                        <Button
                          onClick={() => handleApprove(message.id)}
                          variant="outline"
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Aprobar"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Botón Enviar Ahora - solo si es PENDING_APPROVAL o APPROVED */}
                      {canSend(message) && (
                        <Button
                          onClick={() => handleSendNow(message.id)}
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Enviar Ahora"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
